import { Container } from './Container'
import { Eyebrow } from './Eyebrow'

export interface PageStat {
  value: string
  label: string
}

interface PageHeadProps {
  eyebrow: string
  /** Rendered as the h1. A trailing `emphasis` is set in fairway italic. */
  title: string
  emphasis?: string
  lede: string
  stats?: PageStat[]
}

/**
 * The opening of a clubhouse page: fairway contours behind an eyebrow, a
 * Fraunces h1, a lede, and optionally a rule of statistics.
 *
 * Shared by all four member areas, which is why the contour paths live here
 * rather than being copied per screen.
 */
export function PageHead({ eyebrow, title, emphasis, lede, stats }: PageHeadProps) {
  return (
    <section className="relative overflow-hidden pb-12 pt-16">
      {/* Fairway contours pitched to the foot of the header. `none` rather
          than `slice` so the band maps proportionally instead of being
          cropped, and low in the box so it never crosses the lede — a thin
          horizontal line through running text reads as strikethrough. */}
      <svg
        viewBox="0 0 1200 320"
        preserveAspectRatio="none"
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-0 size-full opacity-40"
      >
        <path d="M-50 250 Q300 228 620 258 T1250 240" stroke="var(--color-sage)" fill="none" strokeWidth="1.3" />
        <path d="M-50 278 Q300 256 620 286 T1250 268" stroke="var(--color-brass)" fill="none" strokeWidth="1" opacity=".45" />
        <path d="M-50 304 Q320 282 640 312 T1250 296" stroke="var(--color-fairway)" fill="none" strokeWidth="1.3" opacity=".55" />
      </svg>

      <Container>
        <Eyebrow>{eyebrow}</Eyebrow>
        <h1 className="mb-3.5 mt-4 font-display text-[clamp(2.375rem,4.8vw,3.625rem)] font-medium leading-[1.04] tracking-[-0.015em] text-pine">
          {title}
          {/* The space lives OUTSIDE the em, as `Masthead` keeps it: the
              accessible-name computation behind getByRole trims an inline
              child's text before concatenating it, so "Always in<em> season.</em>"
              is named "Always inseason." there (a browser keeps the space). */}
          {emphasis && (
            <>
              {' '}
              <em className="italic text-fairway">{emphasis}</em>
            </>
          )}
        </h1>
        <p className="max-w-[60ch] text-[16.5px] text-ink-soft">{lede}</p>

        {stats && (
          <dl className="mt-10 flex max-w-[520px] flex-wrap gap-7 border-t border-brass/35 pt-6">
            {stats.map((stat) => (
              <div key={stat.label}>
                <dd className="font-display text-[28px] text-pine">{stat.value}</dd>
                <dt className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-soft">
                  {stat.label}
                </dt>
              </div>
            ))}
          </dl>
        )}
      </Container>
    </section>
  )
}

/**
 * A clubhouse section opening: eyebrow, then the heading with a brass
 * double-rule running out to the margin.
 */
export function AreaSectionHead({ eyebrow, title }: { eyebrow: string; title: string }) {
  return (
    <div className="mb-6">
      <Eyebrow>{eyebrow}</Eyebrow>
      <div className="mt-2.5 flex items-center gap-5.5">
        {/* nowrap only where a line fits: a long section title on a phone WRAPS
            and the rule shortens beside it — never a sideways-scrolling page. */}
        <h2 className="font-display text-[clamp(1.5rem,3vw,2rem)] font-medium text-pine sm:whitespace-nowrap">
          {title}
        </h2>
        <div aria-hidden="true" className="club-rule min-w-8 flex-1" />
      </div>
    </div>
  )
}
